/**
 * Odkaz z Tandem Zostáv v karte prehliadača odovzdá appke (KOLO 215).
 *
 * Program Tandem Zostavy otvára `/zakazky?koncept=<id>` v predvolenom
 * prehliadači. Keď tento profil Chromu má Rozpočet nainštalovaný ako appku
 * (poznačí si to okno appky), karta namiesto stránky prejde na
 * `web+rozpocet:koncept/<id>` — Chrome spustí appku, otvorené okno dá
 * dopredu a koncept prevezme (`usePrevzatieOdkazu`). Appka potvrdí
 * prevzatie na `BroadcastChannel` a karta sa zavrie.
 *
 * Keď sa do pár sekúnd nič nestane (appka odinštalovaná, Chrome čaká na
 * povolenie), karta ponúkne **Otvoriť v aplikácii** (kliknutie — Chrome
 * bez neho niekedy protokol neotvorí) a **Pokračovať tu** (import v karte
 * ako doteraz). Bez nainštalovanej appky sa nemení nič.
 */
import { useEffect, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import logoFarebne from "@/assets/tandem-logo-farebne.svg";
import {
  KANAL,
  konceptZProtokolu,
  odkazDoAppky,
  odovzdatAppke,
  ostanTu,
  prehliadac,
  zabudniAppku,
} from "@/lib/otvorenieAppky";

/** Ako dlho karta čaká na appku, kým ponúkne tlačidlá. */
export const CAKANIE_MS = 3000;

export function OdkazDoAppky({ children }: { children: ReactNode }): JSX.Element {
  const { pathname, search } = useLocation();
  const [koncept] = useState(() => odovzdatAppke(pathname, search));
  const [tu, setTu] = useState(false);
  if (!koncept || tu) return <>{children}</>;
  return (
    <Odovzdanie
      koncept={koncept}
      onTu={() => {
        ostanTu();
        zabudniAppku();
        setTu(true);
      }}
    />
  );
}

function Odovzdanie({
  koncept,
  onTu,
}: {
  koncept: string;
  onTu: () => void;
}): JSX.Element {
  const [prevzate, setPrevzate] = useState(false);
  const [neslo, setNeslo] = useState(false);

  function otvorAppku(): void {
    prehliadac.prejdi(odkazDoAppky(koncept));
  }

  useEffect(() => {
    let kanal: BroadcastChannel | null = null;
    if (typeof BroadcastChannel === "function") {
      kanal = new BroadcastChannel(KANAL);
      kanal.onmessage = (e: MessageEvent<{ prevzaty?: string }>) => {
        if (e.data?.prevzaty !== koncept) return;
        setPrevzate(true);
        // Karta otvorená zvonka má jeden záznam histórie — smie sa zavrieť
        prehliadac.zavri();
      };
    }
    otvorAppku();
    const casovac = window.setTimeout(() => setNeslo(true), CAKANIE_MS);
    return () => {
      window.clearTimeout(casovac);
      kanal?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [koncept]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background-alt px-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="items-center text-center">
          <img src={logoFarebne} alt="Tandem Rozpočet" className="mb-2 h-10 w-auto" />
          <CardTitle>
            {prevzate ? "Import je v aplikácii Rozpočet" : "Otváram v aplikácii Rozpočet…"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-center text-sm text-muted-foreground">
          {prevzate ? (
            <p>Túto kartu môžeš zavrieť.</p>
          ) : neslo ? (
            <>
              <p>
                Aplikácia sa neotvorila? Chrome sa pri prvom odkaze pýta, či ju
                smie otvoriť — povoľ „Vždy“.
              </p>
              <div className="flex flex-col gap-2">
                <Button onClick={otvorAppku}>Otvoriť v aplikácii</Button>
                <Button variant="outline" onClick={onTu}>
                  Pokračovať tu v prehliadači
                </Button>
              </div>
            </>
          ) : (
            <Loader2 aria-label="Čakám na aplikáciu" className="mx-auto h-6 w-6 animate-spin" />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * `/otvor?odkaz=web+rozpocet:koncept/<id>` — adresa z `protocol_handlers`
 * manifestu, keď Chrome appku spustí nanovo. Koncept prevezme zoznam
 * zákaziek (`?koncept=`), ako odkaz priamo z programu.
 */
export function OtvorOdkaz(): JSX.Element {
  const { search } = useLocation();
  const koncept = konceptZProtokolu(new URLSearchParams(search).get("odkaz"));
  return <Navigate to={koncept ? `/zakazky?koncept=${koncept}` : "/zakazky"} replace />;
}
